import { Button, Flex, Heading, Link, ListItem, OrderedList, Text } from "@chakra-ui/react";
import { type Curves } from "@taquito/signer";
import { type ChainFullId, ChainNamespaces, defaultDerivationPathTemplates } from "@umami/chains";
import { useDynamicModalContext, useMultiForm } from "@umami/components";
import { useAsyncActionHandler, useCreateLedgerWallets, useGetNetworkByFullId } from "@umami/state";
import { defaultDerivationPathTemplate } from "@umami/tezos";
import { FormProvider } from "react-hook-form";

import { useColor } from "../../../styles/useColor";
import { AdvancedAccountSettings } from "../AdvancedAccountSettings";

import { NetworkSelector } from "@/components/NetworkSelector/NetworkSelector";

type FormFields = {
  chainId?: ChainFullId;
  derivationPath: string;
  curve: Curves;
};

const getLedgerAppInfo = {
  [ChainNamespaces.TEZOS]: {
    appName: "Tezos Wallet",
    appUrl: "https://support.ledger.com/article/360016057774-zd",
  },
  [ChainNamespaces.EVM]: {
    appName: "Ethereum Wallet",
    appUrl: "https://support.ledger.com/article/360009576554-zd",
  },
  [ChainNamespaces.BITCOIN]: {
    appName: "Bitcoin Wallet",
    appUrl: "https://support.ledger.com/article/115005195945-zd",
  },
};

export const LedgerTab = () => {
  const color = useColor();

  const createLedgerWallets = useCreateLedgerWallets();
  const { onClose } = useDynamicModalContext();
  const form = useMultiForm<FormFields>({
    mode: "onBlur",
    defaultValues: {
      derivationPath: defaultDerivationPathTemplate,
      curve: "secp256k1",
    },
  });
  const { handleAsyncAction, isLoading } = useAsyncActionHandler();

  const chainId = form.watch("chainId");
  const getNetwork = useGetNetworkByFullId();

  const updateDerivationPath = (chainId: ChainFullId) => {
    form.setValue("chainId", chainId);
    form.setValue("derivationPath", defaultDerivationPathTemplates[getNetwork(chainId).chain]);
  };

  const onSubmit = ({ derivationPath: derivationPathTemplate, curve }: FormFields) =>
    handleAsyncAction(async () => {
      await createLedgerWallets({
        derivationPathTemplate,
        curve,
        chainId,
      });

      return onClose();
    });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {!chainId ? (
          <NetworkSelector isLedger onClick={updateDerivationPath} />
        ) : (
          <Flex flexDirection="column" gap="30px">
            <OrderedList marginLeft="0" listStyleType="none" spacing="12px" variant="explanation">
              <ListItem>
                <Heading color={color("black")} size="lg">
                  1
                </Heading>
                Plug in your ledger into your computer
              </ListItem>

              <ListItem>
                <Heading color={color("black")} size="lg">
                  2
                </Heading>
                Unlock your ledger
              </ListItem>

              <ListItem>
                <Heading color={color("black")} size="lg">
                  3
                </Heading>

                <Text>
                  Ensure your ledger has the{" "}
                  <Link
                    textDecoration="underline"
                    _hover={{ color: color("600") }}
                    href="https://support.ledger.com/article/360002731113-zd"
                    isExternal
                  >
                    latest firmware
                  </Link>{" "}
                  version
                </Text>
              </ListItem>

              <ListItem>
                <Heading color={color("black")} size="lg">
                  4
                </Heading>

                <Text>
                  Install & open the{" "}
                  <Link
                    textDecoration="underline"
                    _hover={{ color: color("600") }}
                    href={getLedgerAppInfo[getNetwork(chainId).namespace].appUrl}
                    isExternal
                  >
                    {getLedgerAppInfo[getNetwork(chainId).namespace].appName}
                  </Link>{" "}
                  app on your ledger
                </Text>
              </ListItem>

              <ListItem>
                <Heading color={color("black")} size="lg">
                  5
                </Heading>
                Click the button below & confirm on your ledger
              </ListItem>
            </OrderedList>

            <AdvancedAccountSettings isCurveEnabled />

            <Button width="full" isLoading={isLoading} type="submit" variant="primary">
              Import wallet
            </Button>
          </Flex>
        )}
      </form>
    </FormProvider>
  );
};
